import React, { ReactNode, useEffect, useState } from "react";

import {
  useToggleLocalMute,
  useLogger,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import { useDevices } from "../../../video/providers/DevicesProvider";
import { Mic, MicOff } from "lucide-react";
import { Type } from "@shared/ui/Type";
import { isOptionActive } from "../../../video/utils/device-utils";
import { DropdownButtonItem } from "@shared/ui/DropdownButtonItem";
import { ButtonDropdownCombo } from "@shared/ui/ButtonDropdownCombo";
import { VideoUIContainerBreakpoints } from "../../videoUI.types";
import { DefaultBrowserBehavior } from "amazon-chime-sdk-js";
import { videoEventBus } from "../../../video/videoEventBus";
import { useBrowserCompatibilityFeatures } from "../../../video/hooks/useBrowserCompatibilityFeatures";

interface Props {
  muteLabel?: string;
  unmuteLabel?: string;
  containerBreakpoints: VideoUIContainerBreakpoints;
}

export const AudioInputControl: React.FC<React.PropsWithChildren<Props>> = ({
  muteLabel = "Mute",
  unmuteLabel = "Unmute",
  containerBreakpoints,
  ...rest
}) => {
  const logger = useLogger();
  const meetingManager = useMeetingManager();
  const { muted, toggleMute } = useToggleLocalMute();
  const { 
    audioInputDevices: devices, 
    selectedAudioInputDevice: selectedDevice,
    audioOutputDevices,
    selectedAudioOutputDevice: audioOutputDevice,
    selectAudioInputDevice,
    selectAudioOutputDevice
  } = useDevices();
  const [dropdownOptions, setDropdownOptions] = useState<ReactNode[]>([]);

  // Get browser compatibility features
  const { supportsAudio, supportsAudioOutput } =
    useBrowserCompatibilityFeatures();

  useEffect(() => {
    const handleClickAudioOutputDevice = async (
      deviceId: string
    ): Promise<void> => {
      try {
        if (new DefaultBrowserBehavior().supportsSetSinkId()) {
          // Update our custom device provider
          await selectAudioOutputDevice(deviceId);
          // Also update the Chime SDK
          await meetingManager.startAudioOutputDevice(deviceId);
        } else {
          logger.error(
            "AudioOutputControl cannot select audio output device because browser does not support setSinkId operation."
          );
        }
      } catch (error) {
        logger.error("AudioOutputControl failed to select audio output device");
      }
    };

    const handleClick = async (deviceId: string): Promise<void> => {
      try {
        // Update our custom device provider
        await selectAudioInputDevice(deviceId);
        // Also update the Chime SDK
        await meetingManager.startAudioInputDevice(deviceId);
        videoEventBus.emitEvent("videochat:audio:source:selected", {
          deviceId,
        });
      } catch (error) {
        logger.error("AudioInputControl failed to select audio input device");
      }
    };

    const getDropdownOptions = async (): Promise<void> => {
      let dropdownOptions = [];

      const dropdownOptionsAudioInput = await Promise.all(
        devices.map(async (device) => (
          <DropdownButtonItem
            key={device.deviceId}
            checked={await isOptionActive(selectedDevice, device.deviceId)}
            onClick={async () => await handleClick(device.deviceId)}
          >
            <span>{device.label}</span>
          </DropdownButtonItem>
        ))
      );

      const dropdownOptionsAudioOutput: ReactNode[] = await Promise.all(
        audioOutputDevices.map(async (device) => (
          <DropdownButtonItem
            key={device.deviceId}
            checked={await isOptionActive(audioOutputDevice, device.deviceId)}
            onClick={async () =>
              await handleClickAudioOutputDevice(device.deviceId)
            }
          >
            <span>{device.label}</span>
          </DropdownButtonItem>
        ))
      );

      if (supportsAudioOutput) {
        dropdownOptions.push(
          <Type
            variant="h6"
            className="text-brand-600 px-4 py-4 border-brand-800"
          >
            Audio Output
          </Type>,
          ...dropdownOptionsAudioOutput
        );
      }

      if (supportsAudio) {
        dropdownOptions.push(
          <Type
            variant="h6"
            className="text-brand-600 px-4 py-4 border-brand-800"
          >
            Audio Input
          </Type>,
          ...dropdownOptionsAudioInput
        );
      }

      setDropdownOptions(dropdownOptions);
    };

    getDropdownOptions();
  }, [
    devices,
    selectedDevice,
    meetingManager,
    meetingManager.startAudioInputDevice,
    selectAudioInputDevice,
    selectAudioOutputDevice,
  ]);

  const handleToggleMute = () => {
    // Track the event BEFORE toggling (based on current state)
    if (muted) {
      videoEventBus.emitEvent("videochat:unmuted", {});
    } else {
      videoEventBus.emitEvent("videochat:muted", {});
    }

    // Then toggle the mute state
    toggleMute();
  };

  return (
    <ButtonDropdownCombo
      buttonIcon={muted ? MicOff : Mic}
      buttonLabel={
        containerBreakpoints && containerBreakpoints.isMd
          ? muted
            ? unmuteLabel
            : muteLabel
          : ""
      }
      buttonOnClick={supportsAudio ? handleToggleMute : () => {}}
      dropdownOptions={supportsAudio ? dropdownOptions || [] : []}
      destructive={muted}
      disabled={!supportsAudio}
      {...rest}
    />
  );
};

export default AudioInputControl;
